import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { InvoiceChargesService } from 'src/app/services/invoice-charges/invoice-charges.service';
import { InvoiceChargesModalService } from 'src/app/services/invoice-charges-modal/invoice-charges-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-proforma-invoice-charges',
  templateUrl: './proforma-invoice-charges.component.html',
  styleUrls: ['./proforma-invoice-charges.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class ProformaInvoiceChargesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  invoiceCharges: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedInvoiceCharges: string[] = [];

  constructor(
    private _invoiceChargesService: InvoiceChargesService,
    private _invoiceChargesModalService: InvoiceChargesModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadInvoiceCharges();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadInvoiceCharges(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.selectedInvoiceCharges = [];

    this.subscriptions.push(
      this._invoiceChargesService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.invoiceCharges = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.invoiceCharges = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading invoice charges:', error);
          this.invoiceCharges = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadInvoiceCharges(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadInvoiceCharges(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadInvoiceCharges();
  }

  openAddInvoiceChargeModal(): void {
    this._invoiceChargesModalService.clearSelectedInvoiceCharge();
    const modalElement = document.getElementById('kt_modal_invoice_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(invoiceCharge: any): void {
    this._invoiceChargesModalService.setSelectedInvoiceCharge(invoiceCharge);
    const modalElement = document.getElementById('kt_modal_invoice_charge_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteInvoiceCharge(invoiceCharge: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${invoiceCharge.label}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(invoiceCharge);
      }
    });
  }

  private performDelete(invoiceCharge: any): void {
    this.subscriptions.push(
      this._invoiceChargesService.DeleteDataById(invoiceCharge._id).subscribe({
        next: () => {
          this.loadInvoiceCharges(this.currentPage, this.searchTerm, this.filters);
          Swal.fire('Deleted!', 'Invoice charge has been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting invoice charge:', error);
          Swal.fire('Error!', 'Failed to delete invoice charge.', 'error');
        }
      })
    );
  }

  bulkDeleteInvoiceCharges(): void {
    if (this.selectedInvoiceCharges.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedInvoiceCharges.length} invoice charge(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedInvoiceCharges.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._invoiceChargesService.BulkDelete(this.selectedInvoiceCharges).subscribe({
        next: () => {
          this.loadInvoiceCharges();
          this.selectedInvoiceCharges = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected invoice charges have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting invoice charges:', error);
          Swal.fire('Error!', 'Failed to delete selected invoice charges.', 'error');
        }
      })
    );
  }

  // Event handlers for modal events
  onInvoiceChargeAdded(): void {
    console.log('Invoice charge added, refreshing list...');
    this.loadInvoiceCharges(this.currentPage, this.searchTerm, this.filters);
  }

  onInvoiceChargeUpdated(): void {
    console.log('Invoice charge updated, refreshing list...');
    this.loadInvoiceCharges(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(invoiceChargeId: string): boolean {
    return this.selectedInvoiceCharges.includes(invoiceChargeId);
  }

  isAllSelected(): boolean {
    return this.invoiceCharges.length > 0 && this.selectedInvoiceCharges.length === this.invoiceCharges.length;
  }

  isIndeterminate(): boolean {
    return this.selectedInvoiceCharges.length > 0 && this.selectedInvoiceCharges.length < this.invoiceCharges.length;
  }

  toggleSelection(invoiceChargeId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedInvoiceCharges.includes(invoiceChargeId)) {
        this.selectedInvoiceCharges.push(invoiceChargeId);
      }
    } else {
      this.selectedInvoiceCharges = this.selectedInvoiceCharges.filter(id => id !== invoiceChargeId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedInvoiceCharges = this.invoiceCharges.map(invoiceCharge => invoiceCharge._id);
    } else {
      this.selectedInvoiceCharges = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-invoice-charge-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-invoice-charge-table-toolbar="selected"]');

    if (this.selectedInvoiceCharges.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  // Math utility for template
  Math = Math;
}
